import { SEOContentHowTo } from "../../../../components/seo/SEOContentHowTo";
import { SEOImage } from "../../../../components/seo/SEOImage";

export const ComoUsarTeleprompterEN = () => (
    <>
        <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight">
            How to Use a Teleprompter on Mobile: Use PromptNinja for Free (Complete Guide)
        </h2>

        <p className="text-lg text-slate-300 mb-6 leading-relaxed">
            Tired of forgetting lines or stuttering in videos and presentations? Using a teleprompter on your phone is the perfect solution for fluid and professional communication. <span className="text-white font-semibold">Start right now, no registration required</span>, and transform the way you express yourself!
        </p>

        <SEOImage
            slug="como-usar-teleprompter-celular"
            src="mobile-teleprompter-use.webp"
            alt="Person holding cell phone using teleprompter"
            caption="PromptNinja turns any smartphone into a powerful and easy-to-use teleprompter."
            width={1200}
            height={675}
        />

        <p className="text-lg text-slate-300 mb-8 leading-relaxed">
            This complete guide will show you the <strong>exact step-by-step</strong> to set up and use a teleprompter on your phone. Let's get straight to the point so you can start speaking with confidence today.
            <br /><br />
            <strong>Ready to master your communication? Let's go!</strong>
        </p>

        <div className="bg-slate-800 p-6 rounded-lg my-8 shadow-lg border border-slate-700">
            <h2 className="text-xl font-bold text-white mb-4">Quick Navigation:</h2>
            <ul className="list-none space-y-3 mb-6 pl-0">
                <li><a href="#why-teleprompter" className="text-blue-400 font-semibold hover:text-blue-300 transition flex items-center gap-2"><span className="text-slate-400 font-normal">1.</span> Why a Teleprompter is Essential?</a></li>
                <li><a href="#step-by-step" className="text-blue-400 font-semibold hover:text-blue-300 transition flex items-center gap-2"><span className="text-slate-400 font-normal">2.</span> <span className="text-green-400">STEP-BY-STEP: How to Use PromptNinja</span></a></li>
                <li><a href="#common-mistakes" className="text-blue-400 font-semibold hover:text-blue-300 transition flex items-center gap-2"><span className="text-slate-400 font-normal">3.</span> Common Mistakes (and How to Avoid Them)</a></li>
                <li><a href="#advanced-tips" className="text-blue-400 font-semibold hover:text-blue-300 transition flex items-center gap-2"><span className="text-slate-400 font-normal">4.</span> Advanced Tips</a></li>
            </ul>
            <a href="/?lang=en#app" className="block w-full md:w-auto text-center bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-6 rounded-lg transition shadow-lg">
                🚀 Go straight to Step-by-Step with PromptNinja!
            </a>
        </div>

        <section id="why-teleprompter" className="mb-12">
            <h3 className="text-2xl font-bold text-white mb-4">The Essentials: Why a Mobile Teleprompter is a Game-Changer?</h3>
            <p className="text-slate-300 mb-4">
                Before we dive into the "how", it's quick to understand "the why". A teleprompter, originally used in TVs and stages, now fits in your pocket. It projects your script subtly, allowing you to maintain eye contact with the camera.
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 mb-4">
                <li><strong>Fluency and Naturalness:</strong> Speak without hesitation, as if you were having a conversation.</li>
                <li><strong>Time Saving:</strong> Fewer takes, fewer edits, more productivity.</li>
                <li><strong>Total Confidence:</strong> Never worry about forgetting an important point again.</li>
            </ul>
        </section>

        <div className="bg-gradient-to-r from-red-900/30 to-green-900/30 p-8 rounded-xl border border-slate-700 mb-12">
            <h3 className="text-3xl font-bold text-white mb-6">First Video: Without vs WITH Teleprompter</h3>
            <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-slate-900/70 p-6 rounded-lg border border-red-500/30">
                    <h3 className="font-bold text-red-400 mb-4 flex items-center gap-2">
                        <span>❌</span> WITHOUT Teleprompter (Memorizing)
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                        <li>🧠 <strong>Prep:</strong> 45min trying to memorize 2min of text</li>
                        <li>🎬 <strong>Recording:</strong> 18 attempts (forgets midway, freezes, loses energy)</li>
                        <li>⏱️ <strong>Total time:</strong> 1h32min to record 2min</li>
                        <li>😓 <strong>Result:</strong> Video published with small "acceptable" stutters</li>
                        <li>💬 <strong>Feedback:</strong> "Seems kinda nervous", "Is he reading something?"</li>
                    </ul>
                </div>
                <div className="bg-slate-900/70 p-6 rounded-lg border border-green-500/30">
                    <h3 className="font-bold text-green-400 mb-4 flex items-center gap-2">
                        <span>✅</span> WITH Teleprompter (First Time Doing it RIGHT)
                    </h3>
                    <ul className="space-y-3 text-slate-300 text-sm">
                        <li>🧠 <strong>Prep:</strong> 3min pasting text, adjusting speed</li>
                        <li>🎬 <strong>Recording:</strong> 2 attempts (1st to test speed, 2nd is the keeper)</li>
                        <li>⏱️ <strong>Total time:</strong> 12min to record 2min</li>
                        <li>😊 <strong>Result:</strong> PERFECT video, zero stutters, energy maintained</li>
                        <li>💬 <strong>Feedback:</strong> "Such confidence!", "Looks like a TV presenter"</li>
                    </ul>
                </div>
            </div>
            <p className="text-slate-400 text-sm mt-6 italic text-center">
                💡 Difference: <strong className="text-green-400">-87% time</strong> (1h32→12min) + <strong className="text-green-400">professional quality</strong> on 1st try.
            </p>
        </div>

        <div id="common-mistakes" className="bg-slate-900 p-6 rounded-lg border border-slate-800 mb-12">
            <h3 className="text-3xl font-bold text-white mb-4">Why Beginners Fail (and How to Avoid It)</h3>
            <p className="text-slate-300 mb-4">
                90% of creators who test teleprompter for the first time QUIT. Not because the tool doesn't work, but because they make 3 classic mistakes:
            </p>
            <div className="space-y-4">
                <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                    <h4 className="font-bold text-red-400 mb-2">Mistake #1: Font Too Large</h4>
                    <p className="text-slate-300 text-sm">
                        They think: "I need to see the text well" and set font size to 72px. Result? Your eyes make a <strong>wide left/right movement</strong> each line. Camera catches it. Audience notices you're reading.
                        <span className="text-green-400 block mt-2">✅ <strong>Solution:</strong> Font 28-36px + stand FARTHER from screen. Eyes move less, looks natural.</span>
                    </p>
                </div>
                <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                    <h4 className="font-bold text-red-400 mb-2">Mistake #2: Text Far from Camera</h4>
                    <p className="text-slate-300 text-sm">
                        They put teleprompter in one corner of screen, camera in another. You record looking 20º to the SIDE of camera. Audience feels you're not talking to THEM.
                        <span className="text-green-400 block mt-2">✅ <strong>Solution:</strong> Stick teleprompter window LITERALLY over camera. Closer the better.</span>
                    </p>
                </div>
                <div className="bg-slate-800 p-4 rounded-lg border-l-4 border-red-500">
                    <h4 className="font-bold text-red-400 mb-2">Mistake #3: Wrong Speed</h4>
                    <p className="text-slate-300 text-sm">
                        They leave on default (usually too fast). You rush to keep up, run out of breath, lose naturalness. Or too slow: giant awkward pauses.
                        <span className="text-green-400 block mt-2">✅ <strong>Solution:</strong> ALWAYS do a 30s test BEFORE recording. Adjust until it matches your natural speaking. There's no "universal correct speed".</span>
                    </p>
                </div>
            </div>
        </div>

        <h3 id="step-by-step" className="text-3xl font-bold text-white mt-12 mb-6">How to Use a Teleprompter: From Basic to Professional</h3>
        <p className="text-slate-300 mb-8">
            There are two main ways to use a teleprompter, especially with web-based tools like PromptNinja, which don't require purchasing expensive equipment.
        </p>

        <div className="mb-12 space-y-8">
            <div className="bg-slate-800 p-6 rounded-lg">
                <h3 className="font-bold text-xl text-white mb-3">Method 1: The Single Device (The Quick Start)</h3>
                <p className="text-slate-400 mb-4">
                    Ideal for beginners or for quick recordings. You use the same device to record and read.
                </p>
                <ol className="list-decimal list-inside space-y-2 text-slate-300">
                    <li>Open your phone's camera or your notebook's webcam.</li>
                    <li>In a window next to it, open <strong>PromptNinja</strong> in your browser.</li>
                    <li>Paste your script, adjust the font size and speed.</li>
                    <li>Position the teleprompter window as close as possible to the camera lens.</li>
                    <li>Start recording, then press play on the teleprompter.</li>
                </ol>
                <p className="text-sm text-slate-500 mt-4"><strong>Disadvantage:</strong> It's hard to control the scroll without interrupting the recording or looking away. Works best for short videos.</p>
            </div>

            <div id="advanced-tips">
                <SEOContentHowTo
                    title="Method 2: The Remote Control (The Professional Way)"
                    schemaTitle="How to Use Teleprompter with Remote Control"
                    tools={["Computer or Tablet", "Smartphone"]}
                    totalTime="PT2M"
                    className="border-2 border-blue-500 rounded-lg p-4"
                    steps={[
                        {
                            title: "Prepare the Main Screen",
                            text: "Open PromptNinja on the device that will serve as the screen (notebook, tablet, or another monitor). This will be the screen you read from."
                        },
                        {
                            title: "Activate Remote Control",
                            text: "On your phone, open PromptNinja and select the \"Remote Control\" option from the main menu."
                        },
                        {
                            title: "Connect Devices",
                            text: "Scan the QR Code that appears on the main screen with your phone's camera. The connection is P2P (Peer-to-Peer) via Wi-Fi, instant and secure."
                        },
                        {
                            title: "Control Your Recording",
                            text: "Done! Your phone is now a remote control. Play, pause, adjust speed, and edit text without leaving your recording position."
                        }
                    ]}
                />
            </div>
            <p className="text-sm text-green-400 mt-2 px-4"><strong>Advantage:</strong> Full control over the recording flow, allowing for dramatic pauses, acceleration, and a much more dynamic and natural presentation.</p>
        </div>

        <h3 className="text-3xl font-bold text-white mt-12 mb-6">Common Mistakes When Using a Teleprompter (and How to Avoid Them)</h3>
        <div className="space-y-4">
            <div className="bg-slate-800 p-4 rounded-lg">
                <h3 className="font-bold text-lg text-white">1. "Dancing" Eyes on the Screen</h3>
                <p className="text-slate-300 mt-2"><strong>The Mistake:</strong> Your eyes visibly move from left to right as you read, giving away that you're using a teleprompter. <br /> <strong>The Solution:</strong> Increase the distance between you and the screen. The farther away you are, the less noticeable the eye movement will be. Also, narrow the width of the text box in the teleprompter so the lines are shorter.</p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg">
                <h3 className="font-bold text-lg text-white">2. Monotonous and Robotic Rhythm</h3>
                <p className="text-slate-300 mt-2"><strong>The Mistake:</strong> You enter "reading mode" and your voice loses emotion, sounding like a robot. <br /> <strong>The Solution:</strong> Don't write the script like a formal text. Use short sentences, punctuation that indicates pauses, and even notes like "(smile here)" or "(pause here)". Practice reading the text aloud once before recording to find the natural rhythm.</p>
            </div>
            <div className="bg-slate-800 p-4 rounded-lg">
                <h3 className="font-bold text-lg text-white">3. Incorrect Scrolling Speed</h3>
                <p className="text-slate-300 mt-2"><strong>The Mistake:</strong> The scroll is too slow, creating awkward pauses, or too fast, causing you to stumble over your words. <br /> <strong>The Solution:</strong> This is where <strong>PromptNinja's P2P remote control</strong> shines. It allows you to adjust the speed in real-time with zero latency. Start a little slower and speed up as you get into the rhythm. A good tip is to always have the next line visible at the top of the screen.</p>
            </div>
        </div>

        <div className="text-center mt-12">
            <a
                href="https://promptninja.solutionkit.com.br"
                className="inline-block bg-gradient-to-r from-blue-500 to-purple-500 font-bold py-3 px-8 mx-auto rounded-full transition hover:brightness-110"
                style={{ color: 'white' }}
            >
                Transform Your Videos Today. Use PromptNinja for Free!
            </a>
            <p className="text-slate-400 mt-4 text-sm">Remote control, no installation, no time limit.</p>
        </div>
    </>
);
